"use client"

/**
 * 03b — Compared. Ahead against a standard check-up, right after "Sound
 * familiar?": the cards above say what people come with, this says why a
 * check-up answers it.
 *
 * Laid out like the NOA comparison Eugene sent (22.09), in the site's own
 * type and colours: short labels on the left, Ahead as an outlined white
 * card in the middle (a wide, faint shadow, same stroke as the row lines,
 * Eugene 22.09), the standard check-up muted on the right. Row 1 is a count
 * (80+ against 15–20). Ahead's other cells are a green check, or a few
 * words where the row has them ("12 organ systems", Sven 08.10); the standard
 * column is a grey dash where a GP panel does not include the test, and a
 * short phrase ("Single values, if indicated") where it can, so no cell claims
 * more than the live compare page does.
 *
 * A real <table> for screen readers. The card is a separate layer behind
 * the middle column, so the rows' rules run under it; the column widths
 * live in `COLS` and the card's `left` / `width` must match them. The two
 * side columns are the same width, so the Ahead card sits on the page's
 * centre line, under the heading and the CTA (Eugene, 29.09).
 */

import type { ReactNode } from "react"
import { BookingCta, PRODUCT_IDS } from "@/components/ui/booking-cta"
import { Button, ArrowRightIcon } from "@/components/ui/button"
import { CheckCircleNeutralIcon } from "@/components/ui/icons"
import { useTranslations } from "@/lib/intl/use-translations"
import { cn } from "@/lib/utils"
import { useHomeV4Copy } from "./home-v4-copy"
import { bookingFlowUrl } from "./home-v4-data"

const COLS = ["w-[35%]", "w-[30%]", "w-[35%]"] as const

/** The nav wordmark from `components/layout/page-shell.tsx`, for the card's head. */
function Wordmark({ label }: { label: string }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 124 35"
      className="mx-auto h-5 w-auto text-neutral-900 md:h-7"
      role="img"
      aria-label={label}
    >
      <path
        fill="currentColor"
        d="M14.34 3.216 2.4 32.158h2.945l3.357-8.27h14.303l3.357 8.27h3.11L17.533 3.216H14.34Zm-4.642 18.15 6.176-15.174 6.138 15.173H9.698Zm32.395-11.123c-3.4 0-5.887 1.738-7.089 4.052V2.386H32.27v29.771h2.735V19.796c0-4.714 3.026-7.153 6.344-7.153 3.318 0 5.722 2.108 5.722 6.326V32.16h2.736V18.64c0-5.707-3.151-8.394-7.71-8.394l-.003-.002Zm21.541.002c-5.264 0-10.197 4.052-10.197 11.04 0 6.99 4.56 11.33 10.406 11.33 4.52 0 8.5-2.314 9.743-6.574h-2.735c-1.202 2.77-3.69 4.219-6.924 4.219-4.311 0-7.504-3.432-7.71-8.23h17.495v-.745c0-7.236-4.727-11.04-10.075-11.04h-.003Zm-7.42 9.55c.332-4.508 3.608-7.236 7.379-7.236 3.77 0 7.088 2.728 7.297 7.236H56.214Zm62.651-17.409v12.487c-1.16-2.647-4.063-4.63-7.752-4.63-5.265 0-9.908 4.135-9.908 11.04 0 6.906 4.518 11.33 9.908 11.33 3.689 0 6.634-2.274 7.752-5.044v4.588h2.735V2.387h-2.735Zm-7.381 27.87c-4.228 0-7.462-3.433-7.462-8.973 0-5.54 3.234-8.682 7.462-8.682 3.773 0 7.42 2.77 7.42 8.682 0 5.913-3.689 8.972-7.42 8.972ZM92.677 10.477H77.15v2.737l15.418-.38a2.445 2.445 0 0 1 2.509 2.49l-.335 16.834h2.694V15.225a4.755 4.755 0 0 0-4.76-4.748h.002Z"
      />
      <path
        fill="currentColor"
        d="M89.47 16.834 76.677 30.626l1.946 1.99L91.49 18.695a1.366 1.366 0 0 0-.094-1.949 1.375 1.375 0 0 0-1.925.089Z"
      />
    </svg>
  )
}

/** A deep green check (included) or a grey dash in a box of the same size.
 * Deep green rather than the olive CheckCircleIcon (Eugene, 23.09). */
function Mark({ included, label }: { included: boolean; label: string }) {
  if (included) {
    return (
      <span
        role="img"
        aria-label={label}
        className="text-deep-green-500 inline-flex"
      >
        <CheckCircleNeutralIcon size={28} />
      </span>
    )
  }
  return (
    <span
      role="img"
      aria-label={label}
      className="inline-flex size-7 items-center justify-center"
    >
      <span className="h-0.5 w-3 rounded-full bg-neutral-300" />
    </span>
  )
}

/** The landing's rows (Eugene, 07.10): checks and dashes as at home, so it
 *  reads at a glance, but each check names what comes with it in a grey
 *  line under the label. Row 1 is the count. `detail` and `standard` say
 *  which rows have that line and a standard cell; the rest get a dash. */
const BLOOD_ROWS = [
  { detail: false, standard: true },
  { detail: true, standard: true },
  { detail: true, standard: true },
  { detail: true, standard: true },
  { detail: true, standard: true },
  { detail: true, standard: false },
  { detail: true, standard: true },
  { detail: false, standard: true },
] as const

/** The `/go/fullbodymri` landing's rows (Eugene, 08.10): the scan against an
 *  MRI through your doctor, by how you get it and what comes with it, as
 *  every MRI is radiation-free. No count row, and labels only, no detail
 *  line (Eugene, 08.10). */
const MRI_ROWS = [
  { standard: true },
  { standard: true },
  { standard: true },
  { standard: true },
  { standard: false },
  { standard: true },
  { standard: false },
] as const

export function HomeV4Compare({
  section,
  planPrice,
  blood = false,
  mri = false,
  cta,
}: {
  section: string
  /** The plan's formatted yearly price, for the button's "from …/year". */
  planPrice: string
  /** The `/go/bloodtest` landing: Ahead's blood test against a standard
   *  one, its own heading and column, no MRI row (Eugene, 07.10). */
  blood?: boolean
  /** The `/go/fullbodymri` landing: Ahead's full-body MRI against an MRI
   *  through your doctor, its own heading, column and rows (Eugene, 08.10). */
  mri?: boolean
  /** Replaces the home button, so a page keeps its own booking and tracking. */
  cta?: ReactNode
}) {
  const { COMPARE: HOME_COMPARE, HERO } = useHomeV4Copy()
  const t = useTranslations("homeV4")
  const landing = blood || mri
  const COMPARE = blood
    ? {
        ...HOME_COMPARE,
        heading: {
          plain: `${t("compareBloodHeadingPlain")} `,
          accent: t("compareBloodHeadingAccent"),
        },
        columns: {
          ...HOME_COMPARE.columns,
          standard: t("compareBloodColStandard"),
        },
      }
    : mri
      ? {
          ...HOME_COMPARE,
          heading: {
            plain: `${t("compareMriHeadingPlain")} `,
            accent: t("compareMriHeadingAccent"),
          },
          columns: {
            ...HOME_COMPARE.columns,
            standard: t("compareMriColStandard"),
          },
        }
      : HOME_COMPARE
  // One shape for both tables: the label, its grey detail line, whether
  // Ahead's cell is the count, words (`ahead`) or a check, and the standard
  // cell's words (null draws a dash).
  type Key = "compareBloodRow2Detail"
  const rows = mri
    ? MRI_ROWS.map((row, i) => ({
        label: t(`compareMriRow${i + 1}Label` as Key),
        detail: null,
        count: false,
        ahead: null,
        standard: row.standard
          ? t(`compareMriRow${i + 1}Standard` as Key)
          : null,
      }))
    : blood
      ? BLOOD_ROWS.map((row, i) => ({
          label: t(`compareBloodRow${i + 1}Label` as Key),
          detail: row.detail ? t(`compareBloodRow${i + 1}Detail` as Key) : null,
          count: i === 0,
          ahead: null,
          standard: row.standard
            ? i === 0
              ? COMPARE.count.standard
              : t(`compareBloodRow${i + 1}Standard` as Key)
            : null,
        }))
      : COMPARE.rows.map((row) => ({
          label: row.label,
          detail: row.detail,
          count: row.kind === "count",
          ahead: row.ahead,
          standard:
            row.kind === "count"
              ? COMPARE.count.standard
              : row.kind === "text"
                ? row.standard
                : null,
        }))
  const last = rows.length - 1

  return (
    // Tighter top to bottom (Eugene, 07.10), the landing's a bit more.
    <section
      className={cn("bg-white", landing ? "py-10 md:py-16" : "py-10 md:py-20")}
    >
      <div className={section}>
        <div className="text-center">
          <p className="type-label-lg text-neutral-500">{COMPARE.eyebrow}</p>
          {/* The accent is its own line from md up, so the break falls
              after the comma, not inside "check-up". */}
          <h2 className="type-display-md-emphasized mx-auto mt-[11px] max-w-[860px] text-balance text-neutral-900">
            {COMPARE.heading.plain}
            <span className="text-deep-green-600 md:block">
              {COMPARE.heading.accent}
            </span>
          </h2>
        </div>

        <div
          className={cn(
            "relative mx-auto mt-10 max-w-[1000px]",
            landing ? "md:mt-10" : "md:mt-12"
          )}
        >
          {/* The Ahead card, from the column head to the last row. */}
          <div
            aria-hidden
            className="absolute inset-y-0 left-[35%] w-[30%] rounded-[24px] bg-white shadow-[0_12px_48px_rgba(0,0,0,0.04)] ring-1 ring-neutral-100"
          />
          <table className="relative w-full table-fixed border-collapse">
            <colgroup>
              {COLS.map((w, i) => (
                <col key={i} className={w} />
              ))}
            </colgroup>
            <thead>
              <tr className="border-b border-neutral-100">
                <td />
                <th
                  scope="col"
                  className={cn("px-3", "pt-6 pb-4 md:pt-7 md:pb-5")}
                >
                  <Wordmark label={COMPARE.columns.ahead} />
                </th>
                <th
                  scope="col"
                  className={cn(
                    "type-body-md md:type-body-lg px-3 text-center font-normal text-neutral-500",
                    "pt-6 pb-4 md:pt-7 md:pb-5"
                  )}
                >
                  {COMPARE.columns.standard}
                </th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row, i) => {
                const pad = cn("py-3.5 md:py-4", i === last && "pb-6 md:pb-7")
                return (
                  <tr
                    key={row.label}
                    className={cn(i < last && "border-b border-neutral-100")}
                  >
                    <th
                      scope="row"
                      className={cn("pr-4 text-left font-normal", pad)}
                    >
                      <span className="type-body-md-emphasized md:type-body-lg-emphasized block text-neutral-900">
                        {row.label}
                      </span>
                      {row.detail && (
                        <span className="type-body-sm md:type-body-md mt-0.5 block text-neutral-500">
                          {row.detail}
                        </span>
                      )}
                    </th>
                    <td className={cn("px-3 text-center", pad)}>
                      {row.count ? (
                        <span className="type-body-lg-emphasized md:type-headline-sm-emphasized text-neutral-900">
                          {COMPARE.count.ahead}
                        </span>
                      ) : row.ahead ? (
                        <span className="type-body-md-emphasized md:type-body-lg-emphasized text-balance hyphens-auto text-neutral-900">
                          {row.ahead}
                        </span>
                      ) : (
                        <Mark included label={COMPARE.included} />
                      )}
                    </td>
                    <td className={cn("px-3 text-center", pad)}>
                      {row.standard ? (
                        <span className="type-body-md md:type-body-lg text-balance hyphens-auto text-neutral-500">
                          {row.standard}
                        </span>
                      ) : (
                        <Mark included={false} label={COMPARE.notIncluded} />
                      )}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>

        {/* The same button as the hero and 04, centred under the table
            (Eugene, 22.09: add a CTA under the block). */}
        <div className={cn("mt-10 flex justify-center", "md:mt-10")}>
          {cta ?? (
            <BookingCta
              productId={PRODUCT_IDS.bloodTest}
              step="product"
              overrideHref={bookingFlowUrl()}
              trackingLabel="home_v4_compare_cta"
              trackingLocation="dev/home-v4"
              className="block w-full sm:inline-block sm:w-auto"
            >
              <Button
                size="lg"
                variant="primary"
                className="group w-full bg-neutral-950 focus-visible:ring-neutral-400 sm:w-auto"
                iconRight={
                  <ArrowRightIcon className="transition-transform duration-200 ease-[cubic-bezier(0.4,0,0.2,1)] group-hover:translate-x-1" />
                }
              >
                {HERO.ctaPrimary(planPrice)}
              </Button>
            </BookingCta>
          )}
        </div>
      </div>
    </section>
  )
}
